import React, { useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { datadogRum } from '@datadog/browser-rum';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { Carousel } from '@dotcom/crown-ui';
import { useAppContext } from '../../context/app-context';
import { setCardFacesLoaded } from '../../context/app-context/app-context-actions';
import { useCardContext } from '../../context/card-context';
import { useCardFaceReadinessStore } from '../../stores/card-face-readiness';
import { useInitializationDataContext } from '../../context/data-context';
import { usePhotoTextZone } from '../../features/card-editor/hooks/use-photo-text-zone';
import { useTextArea } from '../../features/card-editor/hooks/use-text-area';
import { CardFaceData, EditableTextSettings } from '../../global-types';
import { useFeatureFlags, useIsPodProductCode, useLoadCardData } from '../../hooks';
import { useSystemFailureDialog } from '../../hooks/use-system-failure-dialog';
import { enqueueCanvasOperation } from '../../features/edit-history/edit-history';
import { getSizeAfterScaling, setEditableTextHandlers } from '../../utils';
import { getFaceValidationResult, logTemplateEvolvedFace } from '../../utils/canvas-utils';
import { createEditableInput } from '../../utils/canvas-settings';
import { getMaxTextLines } from '../../utils/helper-settings';
import { CardFace } from '../card-face';
import { SystemFailureDialog } from '../system-failure-dialog';
import { CardEditorProps } from './card-editor-types';
import {
  doesCardFaceHasCanvas,
  getEditableTextSettings,
  getPhotoFrameImage,
  getPhotoZoneButton,
  getPhotoZoneSettings,
  setCanvasFaceUniqueId,
} from './card-editor-utils';
import styles from './card-editor.module.scss';
import { addPlaceholders, getEditableTextButton } from './utils';

export const CardEditor = ({
  cardFaceIndex,
  editorSessionKey,
  navigationRef,
  onAfterSlide,
}: CardEditorProps): React.ReactElement => {
  const { cardState } = useCardContext();
  const faceReadiness = useCardFaceReadinessStore((state) => state.faceReadiness);
  const { initializedDataState } = useInitializationDataContext();
  const {
    appState: { cardFacesLoaded },
    appDispatch,
  } = useAppContext();
  const { loadCardData } = useLoadCardData();
  const { IS_TEXTBOX_OVERLAY_ENABLED, IS_POD_TEXT_ZONE_OVERLAY_ENABLED } = useFeatureFlags();
  const { isUK } = initializedDataState;
  const isPodProductCode = useIsPodProductCode();
  const { addTemplateTextArea } = useTextArea();
  const { addPhotoTextZone } = usePhotoTextZone();
  const { handleSystemFailureAction, handleSystemFailureCancel, handleSystemFailureClose } = useSystemFailureDialog();
  const { t } = useTranslation();
  const isLoadingCardFaces = useRef(false);
  const hasLoadedFaceData = useRef(false);
  const hasSignaledFacesLoaded = useRef(false);
  const previousSessionKey = useRef(editorSessionKey);
  const facePreparationPromises = useRef(new WeakMap<CardFaceData, Promise<void>>());

  // Move to hook
  async function addEditableTexts(face: CardFaceData) {
    const canvas = face.canvas.current;
    if (!canvas) {
      return;
    }

    if (IS_POD_TEXT_ZONE_OVERLAY_ENABLED && IS_TEXTBOX_OVERLAY_ENABLED) {
      await Promise.all(
        face.texts.map(async (currentText, index) => {
          await addTemplateTextArea({
            canvas: face.canvas,
            cardFace: face,
            textSettingsIndex: index,
            textAreaSettings: currentText,
            onError: () => {
              throw new Error(`Error adding template text area`);
            },
          });
        }),
      );
    } else {
      const editableTexts = await Promise.all(
        face.texts.map(async (currentText, index) => {
          if (currentText.IsHybrid) {
            return;
          }

          const settings = getEditableTextSettings(currentText, cardState) as EditableTextSettings;
          const editableInput = createEditableInput(settings, face.faceId, index);
          const editIconScale = getSizeAfterScaling(40, canvas);
          const maxLines = getMaxTextLines(currentText);
          return getEditableTextButton(settings, editIconScale, isUK).then((image) => {
            setEditableTextHandlers(image, editableInput, settings.fontSize, maxLines, cardState);
            return [editableInput, image];
          });
        }),
      );

      editableTexts.forEach((text) => {
        if (!text) {
          return;
        }
        canvas.add(...text);
      });
    }
  }

  async function addPhotoZones(cardFace: CardFaceData) {
    const canvas = cardFace.canvas.current;
    if (!canvas) {
      return;
    }
    const photoZonesButtons = await Promise.all(
      cardFace.zones
        .map((photoZone, index) => {
          const zoneSettings = getPhotoZoneSettings(photoZone, index, cardState);
          const zone = new fabric.Rect(zoneSettings);
          zone.set('fill', '#838684');
          canvas.add(zone);
          return getPhotoZoneButton(zone, canvas, isUK);
        })
        .filter((zones) => zones !== undefined),
    );

    canvas.add(...(photoZonesButtons as fabric.Object[]));
  }

  async function addPhotoTextZones(face: CardFaceData) {
    const canvas = face.canvas;
    if (!canvas) {
      return;
    }
    await Promise.all(
      face.texts
        // TODO: Deal w/ this warning
        // eslint-disable-next-line array-callback-return
        .map(async (zone, index) => {
          const isFirstOfMultipleZones = index === 0 && face.texts.length > 1;
          const label = isFirstOfMultipleZones ? t('cardEditor.greeting') : t('cardEditor.message');
          await addPhotoTextZone({ canvas, text: zone, zoneLabel: label, zoneIndex: index });
        }),
    );
  }

  async function addPhotoFrame(face: CardFaceData) {
    const { photoZoneTemplate } = face;
    const canvas = face.canvas.current;
    if (!photoZoneTemplate || photoZoneTemplate === null || !canvas) {
      return;
    }
    const image = await getPhotoFrameImage(face, cardState);
    canvas.add(image);
  }

  useEffect(() => {
    loadCardData();
  }, [loadCardData]);

  const loadCardFace = useCallback(
    async (face: CardFaceData) => {
      setCanvasFaceUniqueId(face);
      const hasCanvas = doesCardFaceHasCanvas(face);
      // If loading card with json from the edit view, we do not need to load the zones.
      if (!hasCanvas || face.originalCanvasJson) {
        return;
      }

      if (face.zones.length > 0) {
        await addPhotoZones(face);
      }
      if (face.photoZoneTemplate !== null) {
        await addPhotoFrame(face);
      }
      if (!isPodProductCode) {
        await addPhotoTextZones(face);
      } else {
        await addEditableTexts(face);
      }
      addPlaceholders(face, initializedDataState, cardState, isUK, isPodProductCode);
    },
    [cardState],
  );

  const prepareCardFace = useCallback(
    (face: CardFaceData): Promise<void> => {
      const existingPreparation = facePreparationPromises.current.get(face);
      if (existingPreparation) {
        return existingPreparation;
      }

      const canvas = face.canvas.current;
      if (!canvas) {
        return Promise.resolve();
      }

      const preparation = enqueueCanvasOperation(canvas, () => loadCardFace(face));
      facePreparationPromises.current.set(face, preparation);
      return preparation;
    },
    [loadCardFace],
  );

  const loadCardFaces = useCallback(async () => {
    // Early-return guard: skip the load when faces are already loaded/loading or the list is empty.
    if (
      hasLoadedFaceData.current ||
      cardFacesLoaded ||
      isLoadingCardFaces.current ||
      cardState.cardFacesList.length === 0
    ) {
      return;
    }
    isLoadingCardFaces.current = true;
    try {
      const loadedFaces = cardState.cardFacesList.map((face) => prepareCardFace(face));
      await Promise.all(loadedFaces);
      hasLoadedFaceData.current = true;

      cardState.cardFacesList.forEach((face) => {
        if (face.originalCanvasJson) {
          return;
        }
        const templateCardFace = initializedDataState.data?.variables.template_data?.Faces.find(
          (templateCardFace) => templateCardFace.FaceId === face.faceId,
        );
        if (!templateCardFace) {
          return;
        }
        const result = getFaceValidationResult(face, templateCardFace);
        // Recoverable: project was saved against an older template version that
        // required fewer objects than the current template. Intentionally log the
        // divergence to Datadog RUM for observability (no throw, no system error
        // dialog) so the user can continue editing and re-add anything missing.
        logTemplateEvolvedFace('load', result);
      });
    } finally {
      isLoadingCardFaces.current = false;
    }
  }, [prepareCardFace, cardState.cardFacesList, cardFacesLoaded]);

  useEffect(() => {
    loadCardFaces();
  }, [loadCardFaces]);

  // Re-arm the load/render signals when cardFacesLoaded is cleared on editor re-entry (true -> false),
  // so the guards work even if this component instance is reused instead of remounted.
  useEffect(() => {
    if (previousSessionKey.current !== editorSessionKey) {
      hasLoadedFaceData.current = false;
      hasSignaledFacesLoaded.current = false;
      facePreparationPromises.current = new WeakMap<CardFaceData, Promise<void>>();
    }
    previousSessionKey.current = editorSessionKey;
  }, [editorSessionKey]);

  // cardFacesLoaded becomes true only once EVERY displayed face's Fabric canvas has finished
  // rendering (signaled from CardFace.onReady), so the save guard and Address button never act
  // on a half-hydrated canvas.
  useEffect(() => {
    const displayedFaces = cardState.cardFacesList.filter((face) => face.editorDisplayIndicator);
    if (displayedFaces.length === 0) {
      return;
    }
    const allRendered = displayedFaces.every((face) =>
      faceReadiness.some(
        (renderedFace) =>
          renderedFace.faceId === face.faceId &&
          renderedFace.faceNumber === face.faceNumber &&
          renderedFace.type === face.type &&
          renderedFace.editorSessionKey === editorSessionKey &&
          renderedFace.status === 'ready',
      ),
    );
    if (!allRendered) {
      hasSignaledFacesLoaded.current = false;
      if (cardFacesLoaded) {
        setCardFacesLoaded(appDispatch, false);
      }
      return;
    }
    if (cardFacesLoaded || hasSignaledFacesLoaded.current) {
      return;
    }
    if (allRendered) {
      hasSignaledFacesLoaded.current = true;
      setCardFacesLoaded(appDispatch, true);
    }
  }, [cardState.cardFacesList, faceReadiness, editorSessionKey, cardFacesLoaded, appDispatch]);

  return (
    <div className={styles['card-editor']}>
      <ErrorBoundary
        onError={(error, info) => {
          datadogRum.addError(error, { componentStack: info.componentStack });
        }}
        FallbackComponent={() => (
          <SystemFailureDialog
            actionButton={handleSystemFailureAction}
            cancelButton={handleSystemFailureCancel}
            onClose={handleSystemFailureClose}
            isOpen={true}
          />
        )}
      >
        <Carousel
          disableKeyboardControls={true}
          slidesToShow={1}
          slidesToScroll={1}
          dragging={false}
          addClass={styles['carousel']}
          slideClass={styles['card-slide']}
          currentSlideIndex={cardFaceIndex}
          onAfterSlide={onAfterSlide}
        >
          {cardState.cardFacesList
            .filter((face) => face.editorDisplayIndicator)
            .map((face: CardFaceData, index: number) => (
              <CardFace
                cardIndex={index}
                cardFace={face}
                editorSessionKey={editorSessionKey}
                prepareFace={prepareCardFace}
                key={`${editorSessionKey}-${face.faceId}-${face.faceNumber}-${face.type}`}
                navigationRef={navigationRef}
              />
            ))}
        </Carousel>
      </ErrorBoundary>
    </div>
  );
};
